import React from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertExecution from '@app/model/alert/AlertExecution';
import CompetitorsPriceRateAlertExecutionNotificationOutput
	from '@app/model/alert/output/CompetitorsPriceRateAlertExecutionNotificationOutput';
import Utilities from '@util/Utilities';
import RateChange from './RateChange';


const CompetitorsPriceRateUpdatedNotificationsFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	alertExecution: AlertExecution,
	notificationOutputs: CompetitorsPriceRateAlertExecutionNotificationOutput[],
	isInDrawer?: boolean
}> = ({ alertExecution, notificationOutputs, isInDrawer }) => {

	const findCompetitor = (competitorId: number) => {
		return alertExecution.alertOutput.competitors!.find(c => c.id === competitorId);
	};

	const formatRoomFound = (fromRoomName: string, toRoomName: string) => {
		if (fromRoomName === toRoomName) {
			return <>{fromRoomName} <span className="text-gray-500">(Same Room)</span></>;
		}

		return <>{fromRoomName} <span className="text-gray-500">→</span> {toRoomName}</>;
	};

	return <section
		className={`${isInDrawer === true ? 'bg-gray-50' : 'bg-white'} mt-8 rounded-xl border border-gray-200 p-6 shadow-sm sm:p-8`}>
		<div>
			<div className="flex items-center justify-between"><h2
				className="text-base font-semibold leading-6 text-gray-950">
				Updates Since Previous Run</h2></div>
			<div className="mt-5 flow-root">
				<div className="overflow-x-auto">
					<div className="inline-block min-w-full align-middle">
						<table className="min-w-full divide-y divide-gray-300 text-gray-500">
							<thead>
							<tr>
								<th scope="col"
									className="whitespace-nowrap py-3 pl-0 pr-3 text-left text-sm font-semibold text-gray-950">
									Date
								</th>
								<th scope="col"
									className="whitespace-nowrap px-2 py-3 text-left text-sm font-semibold text-gray-950">
									Room
								</th>
								<th scope="col"
									className="whitespace-nowrap px-2 py-3 text-left text-sm font-semibold text-gray-950">
									Change
								</th>
								<th scope="col"
									className="whitespace-normal px-2 py-3 text-left text-sm font-semibold text-gray-950">
									Room Found
								</th>
								<th scope="col"
									className="relative whitespace-nowrap py-3 pl-3 pr-0 text-left text-sm font-semibold text-gray-950">
									Percentage Since Last Run
								</th>
							</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
							{notificationOutputs.map(notificationOutput =>
								notificationOutput.competitorPriceRateChanges.filter(change => change.isNew || change.isUpdated).map(change => {
									return <tr key={`${notificationOutput.notificationId}_${change.competitorId}`}>
										<td className="whitespace-normal py-4 pl-0 pr-3 text-sm text-gray-600">
											<div className="font-semibold text-gray-950">{Utilities.formatDateString(notificationOutput.statDate, 'ddd DD MMM', false)}</div>
											<div className="mt-1">
												{findCompetitor(change.competitorId)?.name}
												{change.isNew &&
													<span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">New</span>}
												{change.isUpdated &&
													<span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">Updated</span>}
											</div>
										</td>
										<td className="whitespace-nowrap px-2 py-4 text-sm text-gray-600">
											{Utilities.snakeCaseToWords(change.current.fromRoomType)}
										</td>
										{change.isUpdated && <td className="whitespace-nowrap px-2 py-4 text-sm text-gray-600">
											<span>{Utilities.formatDouble(change.previous.to, 0)}</span>
											<span className="mx-1 text-gray-400">→</span>
											<span>{Utilities.formatDouble(change.current.to, 0)}</span>
											<br />
											<span
												className="text-xs text-gray-400">Initial {Utilities.formatDouble(change.current.from, 0)}</span>
										</td>}
										{change.isNew && <td className="whitespace-nowrap px-2 py-4 text-sm text-gray-600">
											<span>{Utilities.formatDouble(change.current.from, 0)}</span>
											<span className="mx-1 text-gray-400">→</span>
											<span>{Utilities.formatDouble(change.current.to, 0)}</span>
											<br />
											<span
												className="text-xs text-gray-400">First Entry</span>
										</td>}
										<td className="whitespace-normal px-2 py-4 text-sm leading-6 text-gray-600">
											{formatRoomFound(change.current.fromRoomName, change.current.toRoomName)}
										</td>
										{change.isUpdated && <td className="whitespace-nowrap py-4 pl-3 pr-0 text-sm">
											<span><RateChange rate={change.previous.rate} /></span>
											<span className="mx-2 text-gray-300">→</span>
											<span><RateChange rate={change.current.rate} /></span>
										</td>}
										{change.isNew && <td className="whitespace-nowrap py-4 pl-3 pr-0 text-sm">
											<span><RateChange rate={change.current.rate} /></span>
										</td>}
									</tr>;
								}))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</section>;
};

export default CompetitorsPriceRateUpdatedNotificationsFragment;
